/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationOutreach.module.scss';

type Translate = (key: string) => string;

type Feature = {
  idx: string;
  tag: string;
  cardTitle: string;
  title: string;
  lead?: string;
  body: string;
};

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
    <path
      d="M3 7.5h9M8.5 3.5l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getFeatures = (t: Translate): Feature[] => [
  {
    idx: '01',
    tag: 'sequences',
    cardTitle: t('Email Sequences'),
    title: t('Send highly personalized emails, delivered at the right time'),
    lead: t('Send personalized emails and follow-ups, schedule calls and fulfil daily tasks.'),
    body: t(
      'Use Global Database Sales Engagement Platform for your sales outreach. Start more conversations with prospects, customers and future hires with engaging email sequence templates across email, phone, and social media. Create amazing, personalized experiences.',
    ),
  },
  {
    idx: '02',
    tag: 'tasks',
    cardTitle: t('Task management'),
    title: t('Assign, manage, and record tasks quickly and easily'),
    lead: t('Create email or call tasks, mark the due date and assign them to a member of your team.'),
    body: t(
      'Easily create email or call tasks, mark the priority and due date, and assign them to a member of your team. Record all activity and seamlessly integrate all prospecting actions and notes with your CRM.',
    ),
  },
  {
    idx: '03',
    tag: 'testing',
    cardTitle: t('Analytics'),
    title: t('Optimize with A/B testing'),
    lead: t('Analyze outreach results, A/B test your messages and improve team performance.'),
    body: t(
      'A/B test multiple subject lines, messages, calls-to-action, compare the stats, and find templates that work well for you and double down on them. Identify the performance of every email template, share insights with your team, and make the right decisions.',
    ),
  },
  {
    idx: '04',
    tag: 'extension',
    cardTitle: t('Chrome Extension'),
    title: t('Chrome Extension'),
    body: t(
      'Our Chrome extension makes prospecting on any website, LinkedIn or Sales Navigator as simple as possible. Add new prospects to your cadences and personalize messages with a variety of variables.',
    ),
  },
  {
    idx: '05',
    tag: 'contacts',
    cardTitle: t('Prospect data'),
    title: t('Millions of contacts at your fingertips'),
    body: t(
      'Find quality prospects for your outreach campaigns right from the Global Database Prospecting Platform. Of the 600M+ company records on the platform, contact coverage currently spans 130 million companies and 150 million contacts worldwide.',
    ),
  },
];

export const SalesProspectingLeadGenerationOutreach = () => {
  const { t } = useInt();
  const features = getFeatures(t);
  const blockRefs = useRef<(HTMLElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      return undefined;
    }

    let frame: number;
    let last = -1;

    const track = () => {
      const mid = window.innerHeight / 2;
      let best = 0;
      let bestDistance = Infinity;

      blockRefs.current.forEach((node, index) => {
        if (!node) {
          return;
        }

        const rect = node.getBoundingClientRect();
        const distance = Math.abs((rect.top + rect.bottom) / 2 - mid);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      if (best !== last) {
        last = best;
        setActive(best);
      }

      frame = requestAnimationFrame(track);
    };

    frame = requestAnimationFrame(track);

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>05</span> / {t('Outreach')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t(
                'Automate your entire outbound campaigns by creating custom sequences of emails, calls and tasks',
              )}
              description={t(
                'Multi-step sequences, call and task cadences, and one-click enrolment from the browser extension — built on a segment of verified companies, not scraped profiles.',
              )}
            />
          </div>

          <div className={styles.rows}>
            <div className={styles.rail}>
              <div className={styles.cards}>
                {features.map((feature, index) => (
                  <div
                    key={feature.tag}
                    className={cn(styles.card, index === active && styles.cardOn)}
                    aria-hidden="true"
                  >
                    <span className={styles.idx}>{feature.idx}</span>

                    <div className={styles.chapter}>{feature.cardTitle}</div>

                    <span className={styles.pill}>{t(feature.tag)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.ticks}>
                {features.map((feature, index) => (
                  <span key={feature.tag} className={cn(styles.tick, index === active && styles.tickOn)} />
                ))}
              </div>
            </div>

            <div className={styles.stack}>
              {features.map((feature, index) => (
                <article
                  key={feature.tag}
                  ref={(node) => {
                    blockRefs.current[index] = node;
                  }}
                  className={cn(styles.block, index === active && styles.blockOn)}
                >
                  <div className={styles.bhead}>
                    <span className={styles.idx}>{feature.idx}</span>
                    <h3>{feature.title}</h3>
                    <span className={styles.pill}>{t(feature.tag)}</span>
                  </div>

                  {feature.lead && <p className={styles.lead}>{feature.lead}</p>}

                  <p className={styles.text}>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>

          <div className={styles.cta}>
            <div className={styles.ctaGlow} aria-hidden="true" />

            <div className={styles.ctaCopy}>
              <span className={styles.ctaKicker}>{t('Live walkthrough')}</span>
              <h3 className={styles.ctaTitle}>
                {t('Request a live demo presentation to automate your outbound campaigns')}
              </h3>
            </div>

            <div className={styles.ctaActions}>
              <Button
                type="blue"
                size="lg"
                href="https://platform.globaldatabase.com/public/signup/sales_engagement_free_tier"
                suffix={<ArrowIcon />}
              >
                {t('Start for free')}
              </Button>

              <Button type="white-outline" size="lg" href="/book-a-demo">
                {t('Request a demo')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
